import { memo, useCallback, useState } from 'react'
import styled from 'styled-components'
import {
  format,
  subDays,
  startOfDay,
  isBefore,
  isAfter,
  differenceInCalendarDays,
} from 'date-fns'
import { tint, transparentize } from 'polished'

import type { DayModifiers } from 'components/form/DateRangeCalendar'
import { formatPrice } from '../../utils/pricing'
import { findAvailability } from './dayAvailability'

import { DiscountMarker } from './discounts'
import { DaysAvailabilities, FocusedInput } from './types'

const DayNumber = styled.div`
  color: #333;
  font-size: 16px;
`

const Detail = styled.div`
  height: 12px;
  font-size: 11px;
  margin-bottom: 4px;
  line-height: 1;
  color: ${tint(0.4, '#333')};
  overflow: visible;
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  text-align: center;
  align-items: center;
  padding: 4px 0;
  font-weight: 400;

  box-sizing: border-box;
  width: 100%;
  height: 100%;

  // Ensure that focus/active outlines render correctly even when this element
  // has a background set:
  outline: inherit;

  &[data-discount='true'] {
    ${DiscountMarker} {
      position: absolute;
      top: 4px;
      left: 4px;
    }

    ${Detail} {
      color: ${({ theme }) => theme?.['-ds-core-400']?.value};
    }
  }

  // Fully unavailable days (booked, with no check-out-only fallback) get a
  // solid grey fill.
  &[data-unavailable='true'] {
    background: #ececec;
  }

  &[data-check-out-only='true'] {
    background: linear-gradient(
      to bottom right,
      #fff 0%,
      #fff 49.9%,
      #ececec 50%,
      #ececec 100%
    );
  }

  &[data-check-in-only='true'] {
    background: linear-gradient(
      to top left,
      #fff 0%,
      #fff 49.9%,
      #ececec 50%,
      #ececec 100%
    );
  }

  // Hover-preview range: when the user has picked a start date and is
  // hovering over a potential end, every cell from start+1 through the
  // hovered cell (inclusive) gets the same light-teal fill that
  // .rdp-range_middle paints on a fully-selected range, so the user
  // can preview the span before committing the end date.
  &[data-preview-middle='true'] {
    background: var(--rdp-range_middle-background-color);
  }

  // When a check-in-only or check-out-only day is hoverable (i.e. the user
  // could select it) and isn't already selected, drop the diagonal hint so
  // the cell reads like any other available day under the hover paint.
  // Selected cells take precedence via the .rdp-selected rules below.
  // Cells already inside the hover-preview span also keep the range_middle
  // background painted by the data-preview-middle rule above, so they're
  // explicitly excluded here.
  td:not(.rdp-selected)
    button:not([aria-disabled='true']):hover
    &[data-check-out-only='true']:not([data-preview-middle='true']),
  td:not(.rdp-selected)
    button:not([aria-disabled='true']):hover
    &[data-check-in-only='true']:not([data-preview-middle='true']) {
    background: none;
  }

  // Past dates inside the calendar still render so the layout doesn't jump,
  // but the price is hidden because it's no longer meaningful.
  &[data-past='true'] ${Detail} {
    visibility: hidden;
  }

  // Dim the inner text for any disabled day. Fully-unavailable days use a
  // slightly darker text colour than out-of-range / minimum-nights days.
  button[aria-disabled='true'] & {
    ${DayNumber}, ${Detail} {
      color: ${tint(0.7, '#333')};
    }
  }

  &[data-unavailable='true'] {
    ${DayNumber}, ${Detail} {
      color: ${tint(0.4, '#333')};
    }
  }

  // Any selected cell wipes the per-day data-attribute background so the
  // range/selection visuals can take over (rdp v9 only sets the range_*
  // modifiers when both endpoints are picked; a partial-range start is
  // .rdp-selected but not .rdp-range_start, so target .rdp-selected
  // directly to cover both cases).
  td.rdp-selected & {
    background: none;
  }

  // Range-middle cells keep the default dark text on the light-teal
  // middle background, overriding the dim treatment unavailable /
  // disabled days would otherwise carry.
  td.rdp-range_middle & {
    ${DayNumber} {
      color: #333;
    }

    ${Detail} {
      color: ${tint(0.4, '#333')};
    }
  }

  // Hover-preview cells follow the same text-colour treatment as a real
  // range_middle. The leading td bumps specificity above the
  // button:disabled & dim rule so minimum-nights-blocked days inside
  // the preview span don't render as faded.
  td &[data-preview-middle='true'] {
    ${DayNumber} {
      color: #333;
    }

    ${Detail} {
      color: ${tint(0.4, '#333')};
    }
  }

  // Selected non-middle cells (partial-range start, complete range_start,
  // range_end) get the solid accent fill + white inner text. The Wrapper
  // sits over the button so painting it accent here works whether or not
  // rdp's own .rdp-range_start/.rdp-range_end button rules fire.
  td.rdp-selected:not(.rdp-range_middle) & {
    background: var(--rdp-range_start-date-background-color);

    ${DiscountMarker} {
      background-color: ${transparentize(0.6, '#fff')};
    }

    ${DayNumber} {
      color: #fff;
    }

    ${Detail} {
      color: ${transparentize(0.6, '#fff')};
    }
  }
`

type CalendarDayProps = {
  focusedInput: FocusedInput
  day: Date
  modifiers: DayModifiers
  daysData: DaysAvailabilities
  today: Date
  maxStartDate?: Date
  startDate?: Date
  firstUnavailableDate?: Date
  activeDiscount?: DaysAvailabilities[string]['discount']
  // Called when the user clicks a disabled day with an explanatory
  // hint (e.g. "2 night minimum stay"); the parent shows a popover
  // anchored to `referenceElement`.
  onTooltipOpen?: (referenceElement: HTMLElement, message: string) => void
}

const buildHoverHint = ({
  focusedInput,
  day,
  startDate,
  maxStartDate,
  firstUnavailableDate,
  availableForCheckOutOnly,
  beyondFirstUnavailable,
  modifiers,
  dayData,
  startDayMinimumNights,
  inThePast,
  isUnavailable,
}: {
  focusedInput: FocusedInput
  day: Date
  startDate?: Date
  maxStartDate?: Date
  firstUnavailableDate?: Date
  availableForCheckOutOnly: boolean
  beyondFirstUnavailable: boolean
  modifiers: DayModifiers
  dayData?: DaysAvailabilities[string]
  startDayMinimumNights?: number
  inThePast: boolean
  isUnavailable: boolean
}): string | undefined => {
  // No hover hint for past dates, fully-unavailable dates (which can't be
  // selected at all), or non-disabled dates. The disabled styling carries
  // enough meaning on its own in those cases.
  if (modifiers.outside || !modifiers.disabled || inThePast || isUnavailable) {
    return undefined
  }

  const beforeStartDate = startDate && isBefore(day, startOfDay(startDate))

  if (focusedInput === 'startDate' || beforeStartDate) {
    if (maxStartDate && isAfter(day, maxStartDate)) {
      return `Check-in cannot be later than ${format(
        maxStartDate,
        'd MMM yyyy'
      )}`
    }
    if (availableForCheckOutOnly) {
      return 'Checkout only'
    }
    if (dayData && dayData.minimumNights > 1) {
      return `${dayData.minimumNights} night minimum stay`
    }
  } else if (startDate && startDayMinimumNights && startDayMinimumNights > 1) {
    // Selecting an end date: only the days within the minimum-nights
    // window from start get the minimum-nights hint; days beyond the next
    // unavailable date get the "closest checkout" hint instead.
    const withinMinNights =
      isAfter(day, startDate) &&
      differenceInCalendarDays(day, startDate) < startDayMinimumNights
    if (withinMinNights) {
      return `${startDayMinimumNights} night minimum stay`
    }
    if (beyondFirstUnavailable && firstUnavailableDate) {
      return `Closest available checkout is ${format(
        firstUnavailableDate,
        'd MMM yyyy'
      )}`
    }
  } else if (beyondFirstUnavailable && firstUnavailableDate) {
    return `Closest available checkout is ${format(
      firstUnavailableDate,
      'd MMM yyyy'
    )}`
  }

  return undefined
}

const CalendarDay = ({
  focusedInput,
  day,
  modifiers,
  daysData,
  today,
  maxStartDate,
  startDate,
  firstUnavailableDate,
  activeDiscount,
  onTooltipOpen,
}: CalendarDayProps) => {
  // Track the DayNumber element so the TooltipPopover can anchor to the
  // day's number rather than the larger cell — keeps the popover tightly
  // positioned around the date a user has just clicked on.
  const [dayNumberElement, setDayNumberElement] = useState<HTMLElement | null>(
    null
  )
  const inThePast = isBefore(day, today)
  const afterMaxStart = maxStartDate ? isAfter(day, maxStartDate) : false

  const dayData = findAvailability({ day, daysData })
  const isAvailable = dayData?.available !== false

  const previousDayData = findAvailability({
    day: subDays(day, 1),
    daysData,
  })
  const previousDayAvailable = previousDayData?.available !== false
  const availableForCheckInOnly = isAvailable && !previousDayAvailable
  const availableForCheckOutOnly = !isAvailable && previousDayAvailable
  const beyondFirstUnavailable =
    firstUnavailableDate && isAfter(day, firstUnavailableDate)

  // The wrapper tracks hover state internally and surfaces it as a custom
  // `previewMiddle` modifier via rdp, so the booking calendar just needs
  // to read the flag rather than re-derive it from start/end/hover dates.
  const isPreviewMiddle = modifiers.previewMiddle === true

  const inSelectableRange = isAvailable && !inThePast && !beyondFirstUnavailable
  // Only show discount if one exists, if the day is selectable and if the
  // discount is the same as the discount of the selected start date (if one is
  // selected):
  const showDiscount =
    inSelectableRange &&
    dayData?.discount &&
    (!startDate || activeDiscount?.id === dayData.discount.id)

  const startDayData =
    startDate && findAvailability({ day: startDate, daysData })

  // "Fully unavailable" = the day is unavailable AND can't be a check-out
  // (because the previous day is also unavailable). No hover hint shown
  // for genuinely booked days.
  const isUnavailable = !isAvailable && !availableForCheckOutOnly

  const hoverHint = buildHoverHint({
    focusedInput,
    day,
    startDate,
    maxStartDate,
    firstUnavailableDate,
    availableForCheckOutOnly,
    beyondFirstUnavailable,
    modifiers,
    dayData,
    startDayMinimumNights: startDayData?.minimumNights,
    inThePast,
    isUnavailable,
  })

  // The DateRangeCalendar's DayButton override uses aria-disabled rather
  // than HTML disabled on blocked cells, so the click event reaches the
  // inner wrapper normally. rdp's handleDayClick still bails on
  // modifier.disabled, so opening the tooltip here doesn't bleed into a
  // selection.
  const openTooltip = useCallback(() => {
    if (!hoverHint || !dayNumberElement) return
    onTooltipOpen?.(dayNumberElement, hoverHint)
  }, [hoverHint, dayNumberElement, onTooltipOpen])

  return (
    <Wrapper
      data-past={inThePast ? 'true' : undefined}
      data-after-max={afterMaxStart ? 'true' : undefined}
      data-check-in-only={availableForCheckInOnly ? 'true' : undefined}
      data-check-out-only={availableForCheckOutOnly ? 'true' : undefined}
      data-unavailable={isUnavailable ? 'true' : undefined}
      data-preview-middle={isPreviewMiddle ? 'true' : undefined}
      data-discount={showDiscount ? 'true' : undefined}
      title={hoverHint}
      onClick={openTooltip}
    >
      {showDiscount && <DiscountMarker />}
      <DayNumber ref={setDayNumberElement}>{format(day, 'd')}</DayNumber>
      <Detail>{isAvailable && dayData && formatPrice(dayData.price)}</Detail>
    </Wrapper>
  )
}

export default memo(CalendarDay)
